/**
 * @fileoverview Public landing page for Hans.
 *
 * Hero → Product showcase (a tab per audience) → Features → Lesson summary → Social proof → CTA → Footer
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mic, Users, Gauge, GraduationCap,
  BookOpen, Mail, Calendar, Clock, CheckCircle2,
  Building2, Briefcase, ShieldCheck,
  LogIn,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";
import { inviteLanding } from "@/lib/pendingInvite";
import { consumeReturnTo } from "@/lib/returnTo";
import PublicLayout from "@/components/PublicLayout";
import HeroShowcase from "@/components/HeroShowcase";
import RoleChooserDialog from "@/components/auth/RoleChooserDialog";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const WAVE_HEIGHTS = [28, 42, 20, 50, 35, 55, 25, 45, 30, 52, 22, 48, 38, 44, 26, 40];

const SoundwaveLoader = () => (
  <div
    className="min-h-screen flex flex-col items-center justify-center"
    style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b' }}
  >
    <div className="flex items-center justify-center gap-3 mb-10">
      <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
      <span className="text-lg font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Hans</span>
    </div>
    <div className="flex items-end justify-center gap-[3px] h-16">
      {WAVE_HEIGHTS.map((h, i) => (
        <div
          key={i}
          className="soundwave-bar"
          style={{ height: `${h}px`, animationDelay: `${i * 0.07}s` }}
        />
      ))}
    </div>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [processingAuth, setProcessingAuth] = useState(
    () => !!new URLSearchParams(window.location.search).get("code")
  );
  // One dialog, opened by both "Get Started" buttons (hero and final CTA).
  const [chooserOpen, setChooserOpen] = useState(false);

  const handleOAuthCallback = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (!code) return;
    window.history.replaceState(null, "", window.location.pathname);
    // Stashed by the signup page before the Google redirect — the ToS
    // acceptance can't survive the round-trip any other way.
    const tosAccepted = sessionStorage.getItem("hans_tos_accepted") === "1";
    sessionStorage.removeItem("hans_tos_accepted");
    try {
      const result = await axios.post(
        `${API}/auth/google`,
        {
          code,
          redirect_uri: window.location.origin,
          ...(tosAccepted ? { tos_accepted: true } : {}),
        },
        { withCredentials: true }
      );
      const user = result.data.user;
      // A Founding Ensemble invitation is redeemed server-side by whichever
      // sign-in the teacher happens to use, so it can land HERE — on the Google
      // round trip — with nothing of the invitation carried through the
      // redirect. When it does, open the founding welcome over the dashboard
      // rather than dropping them into an app that has silently changed plan.
      // An invitation stashed before the Google redirect is the reason this
      // person is here at all — the OAuth round trip carries none of it, so
      // the stash is the only thing that knows. A brand-new Google account
      // owes onboarding first, and for it the stash waits for onboarding's
      // last step rather than being spent here.
      navigate(inviteLanding(user) || consumeReturnTo() || "/dashboard", {
        state: { user, ...(result.data.founding_claimed === true ? { welcome: true } : {}) },
      });
      const greeting = (user.first_name || user.name?.split(/\s+/)[0] || "").trim();
      toast.success(greeting ? `Welcome, ${greeting}!` : "Welcome!");
    } catch (error) {
      console.error("Auth error:", error);
      // A refusal (Google has not verified the address, terms not accepted)
      // says what to do next; retrying the same account gets the same answer.
      toast.error(getErrorMessage(error, "Authentication failed"));
      setProcessingAuth(false);
    }
  }, [navigate]);

  const waveHeights = useMemo(
    () => Array.from({ length: 24 }, () => Math.random() * 50 + 15),
    []
  );

  useEffect(() => {
    handleOAuthCallback();
  }, [handleOAuthCallback]);

  if (processingAuth) return <SoundwaveLoader />;

  return (
    <PublicLayout>
      <RoleChooserDialog open={chooserOpen} onOpenChange={setChooserOpen} />

      {/* ── Hero ── */}
      <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-16 md:pb-24 max-w-5xl mx-auto text-center safe-area-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1
            className="text-4xl md:text-5xl lg:text-7xl font-light tracking-tight leading-[1.1] mb-4 md:mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            The digital studio for the
            <br />
            <span className="text-[#f4cf08]">dedicated artist</span>
          </h1>

          <p className="text-base md:text-lg text-zinc-400 max-w-xl mx-auto mb-8 md:mb-12 leading-relaxed px-4">
            Capture your lessons, review clear summaries, and keep students on track.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              data-testid="get-started-button"
              onClick={() => setChooserOpen(true)}
              className="flex min-w-[220px] touch-target haptic items-center justify-center gap-2
                         rounded-full bg-[var(--accent-yellow)] px-6 py-3 text-sm font-medium
                         text-[var(--text-inverse)] shadow-[0_0_40px_var(--accent-yellow-glow)]
                         transition-transform hover:scale-105 active:scale-95 md:px-8"
            >
              Get Started
            </button>
            <Link
              to="/login"
              className="flex min-w-[220px] touch-target items-center justify-center gap-2 rounded-full
                         border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-6 py-3
                         text-sm font-medium text-[var(--text-primary)] transition-all
                         hover:bg-[var(--studio-overlay)] active:scale-95 md:px-8"
            >
              <LogIn className="h-4 w-4" strokeWidth={1.5} /> Sign in
            </Link>
          </div>
        </motion.div>

        {/* Soundwave */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="flex justify-center items-end gap-[2px] md:gap-[3px] h-16 md:h-20 mt-12 md:mt-20"
        >
          {waveHeights.map((h, i) => (
            <motion.div
              key={i}
              className="w-1 bg-[#f4cf08]/60 rounded-full"
              animate={{ height: [8, h, 8] }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: i * 0.05,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.div>
      </main>

      {/* ── Product showcase ──
          One frame, a tab per audience: organizations and instructors see
          their one-minute launch films, students their introduction, and
          each tab links to its walkthrough on the Onboarding hub. */}
      <section className="relative z-10 px-4 md:px-8 pb-20 md:pb-32 max-w-5xl mx-auto">
        {/* Glow behind the frame */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none">
          <div className="w-[600px] h-[400px] mx-auto rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(244,207,8,0.06) 0%, transparent 70%)' }} />
        </div>

        <motion.div {...fadeInUp} className="relative">
          <HeroShowcase />
        </motion.div>
      </section>

      {/* ── Features ── */}
      <section className="relative z-10 px-4 md:px-8 pb-20 md:pb-32 max-w-6xl mx-auto safe-area-padding">
        <motion.div {...fadeInUp}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                icon: <Mic className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />,
                title: "Lesson summaries",
                description: "Record any lesson. Hans drafts the summary and practice items for you to review and share.",
              },
              {
                icon: <Users className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />,
                title: "Student Management",
                description: "Organized student profiles with lesson history, notes, and progress tracking.",
              },
              {
                icon: <Gauge className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />,
                title: "Practice Tools",
                description: "Professional metronome and chromatic tuner built for the studio.",
              },
              {
                icon: <BookOpen className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />,
                title: "Repertoire Assistant",
                description: "Teaching guides tailored to your students.",
              },
              {
                icon: <GraduationCap className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />,
                title: "Student Portal",
                description: "Students access their own dashboard with lessons, practice logs, and portfolio.",
              },
              {
                icon: <Mail className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.5} />,
                title: "Email Summaries",
                description: "Send polished lesson summaries to students and parents with one click.",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: index * 0.08, duration: 0.6 }}
                className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 md:p-8
                           hover:border-white/10 transition-colors"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/5 rounded-xl flex items-center justify-center mb-4 md:mb-5">
                  <span className="text-zinc-400">{feature.icon}</span>
                </div>
                <h3 className="text-base md:text-lg font-medium text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Example Lesson Summary ── */}
      <section className="relative z-10 px-4 md:px-8 pb-20 md:pb-32 max-w-5xl mx-auto">
        <motion.div {...fadeInUp} className="text-center mb-10 md:mb-14">
          <h2
            className="text-2xl md:text-4xl font-light tracking-tight mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Every lesson, <span className="text-[#f4cf08]">captured</span>
          </h2>
          <p className="text-sm md:text-base text-zinc-500 max-w-lg mx-auto">
            Hans captures each session and drafts an actionable summary. You review it, then share it for your students to reference between lessons.
          </p>
        </motion.div>

        <motion.div
          {...fadeInUp}
          className="relative"
          style={{ perspective: '1200px' }}
        >
          <div
            className="bg-[#0f0f11] border border-white/8 rounded-2xl overflow-hidden shadow-2xl"
            style={{ transform: 'rotateX(1.5deg)', transformOrigin: 'center bottom' }}
          >
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/5">
              <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <div className="flex-1 mx-3">
                <div className="bg-white/5 rounded-md h-5 max-w-[200px] mx-auto" />
              </div>
            </div>

            <div className="p-4 md:p-6">
              {/* Lesson header */}
              <div className="mb-4 md:mb-5">
                <p className="text-sm md:text-xl font-light text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Chopin Nocturne in E-flat Major — Op. 9 No. 2
                </p>
                <div className="flex items-center gap-3 text-[10px] md:text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Wed, Jan 15</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 45m 30s</span>
                  <span className="text-zinc-400">Emily Chen</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                {/* Left column */}
                <div className="md:col-span-2 space-y-3">
                  {/* Summary */}
                  <div className="bg-white/2 rounded-xl p-3 md:p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5} />
                      <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Summary</p>
                    </div>
                    <p className="text-[11px] md:text-xs text-zinc-300 leading-relaxed">
                      Great progress on the opening section. Rubato is becoming more natural and expressive.
                      Focus next week on left-hand accompaniment evenness through the middle section, particularly
                      measures 12–20. Dynamic contrast between the A and B sections has improved significantly.
                    </p>
                  </div>

                  {/* Key Elements */}
                  <div className="bg-white/2 rounded-xl p-3 md:p-4">
                    <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-2">Key Elements</p>
                    <div className="space-y-1.5">
                      {["Improved tempo control and rubato", "Stronger dynamic contrast", "Clean pedaling through transitions", "Expressive phrasing in melody"].map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="w-4 h-4 md:w-5 md:h-5 shrink-0 bg-[#f4cf08]/15 text-[#f4cf08] rounded-full flex items-center justify-center text-[8px] md:text-[9px] font-medium">{i + 1}</span>
                          <span className="text-[10px] md:text-xs text-zinc-400">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Practice Items */}
                  <div className="bg-[#f4cf08]/3 border border-[#f4cf08]/10 rounded-xl p-3 md:p-4">
                    <p className="text-[9px] md:text-[10px] text-[#f4cf08]/70 font-mono uppercase tracking-widest mb-2">Practice Items</p>
                    <div className="space-y-1.5">
                      {[
                        "Slow practice: LH alone mm. 12–20 at 60 BPM",
                        "Record yourself on the B section and listen back",
                        "Hands together mm. 45–50 with metronome",
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#f4cf08]/50 shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-[10px] md:text-xs text-zinc-400">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-3">
                  {/* Core Concepts */}
                  <div className="bg-white/2 rounded-xl p-3 md:p-4">
                    <p className="text-[9px] md:text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-2">Core Concepts</p>
                    <div className="flex flex-wrap gap-1.5">
                      {["Rubato", "Voicing", "Dynamics", "Phrasing", "Pedaling"].map((c) => (
                        <span key={c} className="text-[9px] md:text-[10px] bg-white/5 text-zinc-400 px-2 py-0.5 rounded-full">{c}</span>
                      ))}
                    </div>
                  </div>

                  {/* Email sent badge */}
                  <div className="bg-white/2 rounded-xl p-3 md:p-4 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-emerald-500/60" strokeWidth={1.5} />
                    <span className="text-[10px] md:text-xs text-emerald-500/60">Sent to student</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── For Organizations ──
          Schools and studios buy differently from a solo instructor, so this band
          speaks to the director rather than the teacher and routes into the four
          /features/organization-* pages. New markup, so it's on the Studio Quiet
          tokens; headings inherit PublicLayout's color like every other section. */}
      <section className="relative z-10 px-4 md:px-8 pb-20 md:pb-32 max-w-6xl mx-auto safe-area-padding">
        <motion.div {...fadeInUp} className="text-center mb-10 md:mb-14">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#f4cf08]/70 mb-3 block">
            For Organizations
          </span>
          <h2
            className="text-2xl md:text-4xl font-light tracking-tight mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Running a school, studio, or <span className="text-[#f4cf08]">program</span>?
          </h2>
          <p className="text-sm md:text-base text-[var(--text-tertiary)] max-w-xl mx-auto">
            Hans gives your organization its own portal: publish and staff programs, build a term
            around the school calendar, and get back a complete record of what was taught.
          </p>
        </motion.div>

        <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {[
            {
              icon: <Building2 className="w-5 h-5" strokeWidth={1.5} />,
              title: "Organization Portal",
              description:
                "Lessons delivered, students reached, and consent coverage across your whole roster — plus feature policy and an audit log of every admin action.",
              to: "/features/organization-portal",
            },
            {
              icon: <Briefcase className="w-5 h-5" strokeWidth={1.5} />,
              title: "Programs & Classes",
              description:
                "Publish a program, staff it, and build the term around the school calendar. Fill the roster from a spreadsheet; program lessons roll up to you automatically.",
              to: "/features/organization-programs",
            },
            {
              icon: <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />,
              title: "Consent & Compliance",
              description:
                "Recording is blocked until consent is granted. COPPA notices for under-13 students, full lesson audio deleted after processing, and an exportable consent ledger.",
              to: "/features/organization-compliance",
            },
          ].map((item, index) => (
            <motion.div
              key={item.to}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: index * 0.08, duration: 0.6 }}
            >
              <Link
                to={item.to}
                className="group flex h-full gap-4 rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)]
                           p-6 transition-colors hover:border-[var(--studio-divider-strong)]"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--studio-interactive)] text-[var(--text-secondary)] transition-colors group-hover:text-[#f4cf08]">
                  {item.icon}
                </div>
                <div>
                  <h3 className="mb-2 text-base md:text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[var(--text-tertiary)]">{item.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <motion.div {...fadeInUp} className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {/* The overview leads, because a director arriving here has not
              decided to sign up yet — they have decided to find out. */}
          <Link
            to="/for-organizations"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--accent-yellow)] px-6 py-3
                       text-sm font-medium text-[var(--text-inverse)] transition-transform
                       hover:scale-105 active:scale-95"
          >
            See how Hans works for organizations
          </Link>
          <Link
            to="/org/signup"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--studio-divider-strong)]
                       bg-[var(--studio-raised)] px-6 py-3 text-sm font-medium transition-all
                       hover:bg-[var(--studio-overlay)] active:scale-95"
          >
            Create your organization
          </Link>
        </motion.div>
      </section>

      {/* ── Social Proof ── */}
      <section className="relative z-10 px-4 md:px-8 pb-20 md:pb-32 max-w-4xl mx-auto">
        <motion.div {...fadeInUp} className="flex items-center gap-4 md:gap-6 justify-center">
          <div className="flex-1 h-px bg-linear-to-r from-transparent to-white/10" />
          <p className="text-[10px] md:text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">
            Trusted by music educators worldwide
          </p>
          <div className="flex-1 h-px bg-linear-to-l from-transparent to-white/10" />
        </motion.div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10 px-4 md:px-8 pb-20 md:pb-32 max-w-3xl mx-auto text-center">
        {/* Glow */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none">
          <div className="w-[500px] h-[300px] mx-auto rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(244,207,8,0.04) 0%, transparent 70%)' }} />
        </div>

        <motion.div {...fadeInUp} className="relative">
          <h2
            className="text-2xl md:text-4xl font-light tracking-tight mb-4 md:mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to elevate your studio?
          </h2>
          <p className="text-sm md:text-base text-zinc-500 max-w-md mx-auto mb-8 md:mb-10">
            Join Hans and spend less time on admin, more time on music.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              data-testid="final-cta-button"
              onClick={() => setChooserOpen(true)}
              className="flex min-w-[220px] touch-target haptic items-center justify-center gap-2
                         rounded-full bg-[var(--accent-yellow)] px-6 py-3 text-sm font-medium
                         text-[var(--text-inverse)] shadow-[0_0_40px_var(--accent-yellow-glow)]
                         transition-transform hover:scale-105 active:scale-95 md:px-8"
            >
              Get Started
            </button>
            <Link
              to="/login"
              className="flex min-w-[220px] touch-target items-center justify-center gap-2 rounded-full
                         border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-6 py-3
                         text-sm font-medium text-[var(--text-primary)] transition-all
                         hover:bg-[var(--studio-overlay)] active:scale-95 md:px-8"
            >
              <LogIn className="h-4 w-4" strokeWidth={1.5} /> Sign in
            </Link>
          </div>
        </motion.div>
      </section>

    </PublicLayout>
  );
};

export default LandingPage;
